import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, LogOut, Plus, Pencil, Trash2, Upload } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { Reveal } from "@/components/Reveal";
import { api, eur } from "@/lib/api";
import { uploadImage, uploadErrorMessage } from "@/lib/upload";
import AdminShipping from "@/components/AdminShipping";
import { useLang } from "@/context/LangContext";

const EMPTY_FORM = {
  title: "",
  dwg_no: "",
  dimensions: "40.6 × 50.8 cm / 16 × 20 in",
  price: "",
  medium: "Encre & graphite sur toile de lin brut",
  year: "2026",
  availability: "In Atelier",
  description: "",
  story: "",
  image_url: "",
  slides: [],
  featured: false,
  title_en: "",
  medium_en: "",
  description_en: "",
  story_en: "",
};

const inputCls =
  "w-full border border-bone/15 bg-ink px-3 py-2.5 font-mono text-xs text-bone placeholder:text-bone-muted/60 focus:border-brass/60 focus:outline-none";

export default function Admin() {
  const { adminToken, setAdminToken, artworks, refreshArtworks, refreshSettings } = useStore();
  const { t } = useLang();
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("artworks");
  const [form, setForm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [siteUploading, setSiteUploading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const headers = { Authorization: `Bearer ${adminToken}` };

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/login", { password });
      setAdminToken(data.token);
      toast.success(t("admin.welcome"));
    } catch {
      toast.error(t("admin.wrongPw"));
    }
  };

  useEffect(() => {
    if (!adminToken) return;
    if (tab === "orders") api.get("/admin/orders", { headers }).then((r) => setOrders(r.data)).catch(() => {});
    if (tab === "inquiries") api.get("/admin/inquiries", { headers }).then((r) => setInquiries(r.data)).catch(() => {});
    if (tab === "site") api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    if (tab === "subscribers") api.get("/admin/subscribers", { headers }).then((r) => setSubscribers(r.data)).catch(() => {});
  }, [tab, adminToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSettings = async (next, msg = t("admin.sitePhotoSaved")) => {
    try {
      const { data } = await api.put("/admin/settings", { ...settings, ...next }, { headers });
      setSettings(data);
      refreshSettings();
      toast.success(msg);
    } catch {
      toast.error(t("admin.saveErr"));
    }
  };

  const exportCsv = () => {
    const rows = ["email,lang,created_at", ...subscribers.map((s) => `${s.email},${s.lang},${s.created_at}`)];
    const url = URL.createObjectURL(new Blob([rows.join("\n")], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: "demoivre-abonnes.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };

  const uploadSitePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSiteUploading(true);
    try {
      const url = await uploadImage(file, headers);
      await saveSettings({ atelier_photo_url: url });
    } catch (err) {
      toast.error(uploadErrorMessage(err, t("admin.uploadErr")));
    } finally {
      setSiteUploading(false);
      e.target.value = "";
    }
  };

  const saveArtwork = async (e) => {
    e.preventDefault();
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (form.artwork_id) {
        await api.put(`/admin/artworks/${form.artwork_id}`, payload, { headers });
        toast.success(t("admin.saved"));
      } else {
        await api.post("/admin/artworks", payload, { headers });
        toast.success(t("admin.created"));
      }
      setForm(null);
      refreshArtworks();
    } catch {
      toast.error(t("admin.saveErr"));
    }
  };

  const deleteArtwork = async (id) => {
    try {
      await api.delete(`/admin/artworks/${id}`, { headers });
      toast.success(t("admin.deleted"));
      refreshArtworks();
    } catch {
      toast.error(t("admin.deleteErr"));
    }
  };

  const uploadPhoto = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const url = await uploadImage(file, headers);
        setForm((f) => ({ ...f, slides: [...(f.slides || []), url], image_url: f.image_url || url }));
      }
      toast.success(t("admin.uploadOk"));
    } catch (err) {
      toast.error(uploadErrorMessage(err, t("admin.uploadErr")));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const toggleAvailability = async (art) => {
    const availability = art.availability === "In Atelier" ? "Unavailable" : "In Atelier";
    try {
      await api.put(`/admin/artworks/${art.artwork_id}`, { ...art, availability }, { headers });
      toast.success(t(`avail.${availability}`));
      refreshArtworks();
    } catch {
      toast.error(t("admin.saveErr"));
    }
  };

  if (!adminToken) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 bp-grid">
        <Reveal>
          <form
            data-testid="admin-login-form"
            onSubmit={login}
            className="w-full max-w-sm border border-bone/15 bg-ink-2 p-10 text-center"
          >
            <p className="font-cursive text-4xl text-bone">DeMoivre</p>
            <p className="mt-2 font-mono text-[9px] tracking-[0.35em] text-bone-muted">{t("admin.access")}</p>
            <input
              data-testid="admin-login-password-input"
              type="password"
              required
              placeholder={t("admin.passwordPh")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputCls} mt-8 text-center tracking-[0.3em]`}
            />
            <button
              data-testid="admin-login-submit"
              type="submit"
              className="mt-4 w-full border border-brass/60 bg-brass/10 py-3.5 font-mono text-[10px] tracking-[0.35em] text-brass transition-all hover:bg-brass hover:text-ink"
            >
              {t("admin.enter")}
            </button>
          </form>
        </Reveal>
      </div>
    );
  }

  const tabs = [
    ["artworks", t("admin.tabArtworks")],
    ["orders", t("admin.tabOrders")],
    ["inquiries", t("admin.tabInquiries")],
    ["subscribers", t("admin.tabSubscribers")],
    ["shipping", t("admin.tabShipping")],
    ["site", t("admin.tabSite")],
  ];
  const SOCIALS = ["instagram", "tiktok", "x", "facebook", "pinterest", "youtube"];
  const publicBase = settings?.site_url || window.location.origin;

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-7xl px-6 pb-28 pt-32 sm:px-10 lg:px-16">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow">{t("admin.eyebrow")}</p>
          <h1 className="mt-3 font-serif text-3xl font-light text-bone sm:text-4xl">{t("admin.title")}</h1>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={() => setAdminToken("")}
          className="flex items-center gap-2 border border-bone/15 px-4 py-2 font-mono text-[10px] tracking-[0.25em] text-bone-dim transition-colors hover:border-brass/50 hover:text-brass"
        >
          <LogOut size={12} /> {t("admin.logout")}
        </button>
      </div>

      <div className="mt-10 flex gap-6 border-b border-bone/10">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`pb-3 font-mono text-[10px] tracking-[0.3em] transition-colors ${
              tab === key ? "border-b border-brass text-brass" : "text-bone-muted hover:text-bone"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "artworks" && (
        <div className="mt-10">
          <button
            data-testid="admin-add-artwork-button"
            onClick={() => setForm({ ...EMPTY_FORM })}
            className="mb-8 flex items-center gap-2 border border-brass/60 bg-brass/10 px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-brass transition-all hover:bg-brass hover:text-ink"
          >
            <Plus size={13} /> {t("admin.addPiece")}
          </button>

          {form && (
            <form
              data-testid="admin-artwork-form"
              onSubmit={saveArtwork}
              className="mb-12 grid grid-cols-1 gap-3 border border-brass/30 bg-ink-2 p-6 sm:grid-cols-2 sm:p-8"
            >
              <p className="font-mono text-[10px] tracking-[0.3em] text-brass sm:col-span-2">
                {form.artwork_id ? `${t("admin.editPiece")} — ${form.dwg_no}` : t("admin.newPiece")}
              </p>
              <input data-testid="admin-field-title" required placeholder={t("admin.fTitle")} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              <input data-testid="admin-field-dwg" placeholder={t("admin.fDwg")} value={form.dwg_no} onChange={(e) => setForm({ ...form, dwg_no: e.target.value })} className={inputCls} />
              <input data-testid="admin-field-dimensions" required placeholder={t("admin.fDimensions")} value={form.dimensions} onChange={(e) => setForm({ ...form, dimensions: e.target.value })} className={inputCls} />
              <input data-testid="admin-field-price" required type="number" min="0" step="1" placeholder={t("admin.fPrice")} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
              <input placeholder={t("admin.fMedium")} value={form.medium} onChange={(e) => setForm({ ...form, medium: e.target.value })} className={inputCls} />
              <input placeholder={t("admin.fYear")} value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className={inputCls} />
              <select data-testid="admin-field-availability" value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} className={inputCls}>
                <option>In Atelier</option>
                <option>Unavailable</option>
                <option>Reserved</option>
                <option>Sold</option>
              </select>
              <div className="sm:col-span-2">
                <div className="flex flex-wrap items-center gap-3">
                  <label className={`flex items-center gap-2 border border-brass/50 px-4 py-2.5 font-mono text-[10px] tracking-[0.25em] text-brass transition-colors ${uploading ? "opacity-50" : "cursor-pointer hover:bg-brass hover:text-ink"}`}>
                    <Upload size={12} />
                    {uploading ? t("admin.uploading") : t("admin.uploadLabel")}
                    <input
                      data-testid="admin-field-image-upload"
                      type="file"
                      multiple
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                      className="hidden"
                      onChange={uploadPhoto}
                      disabled={uploading}
                    />
                  </label>
                  <span className="font-mono text-[9px] tracking-[0.25em] text-bone-muted">{t("admin.slidesHint")}</span>
                </div>
                {(form.slides || []).length > 0 && (
                  <ul data-testid="admin-slides-list" className="mt-3 flex flex-wrap gap-3">
                    {form.slides.map((src, i) => (
                      <li key={src + i} data-testid={`admin-slide-${i}`} className="group relative">
                        <img src={src} alt="" className={`h-24 w-20 border object-cover ${i === 0 ? "border-brass/70" : "border-bone/10"}`} />
                        {i === 0 && <span className="absolute left-0 top-0 bg-brass px-1 font-mono text-[7px] tracking-[0.2em] text-ink">{t("admin.cover")}</span>}
                        <div className="mt-1 flex justify-between gap-1">
                          <button type="button" data-testid={`admin-slide-left-${i}`} disabled={i === 0} onClick={() => setForm((f) => { const sl = [...f.slides]; [sl[i - 1], sl[i]] = [sl[i], sl[i - 1]]; return { ...f, slides: sl, image_url: sl[0] }; })} className="flex h-7 w-7 items-center justify-center border border-bone/15 font-mono text-xs text-bone-dim hover:border-brass/60 hover:text-brass disabled:opacity-20" aria-label="←">←</button>
                          <button type="button" data-testid={`admin-slide-remove-${i}`} onClick={() => setForm((f) => { const sl = f.slides.filter((_, j) => j !== i); return { ...f, slides: sl, image_url: sl[0] || "" }; })} className="flex h-7 w-7 items-center justify-center border border-bone/15 font-mono text-xs text-bone-dim hover:border-red-400/50 hover:text-red-300" aria-label="×">×</button>
                          <button type="button" data-testid={`admin-slide-right-${i}`} disabled={i === form.slides.length - 1} onClick={() => setForm((f) => { const sl = [...f.slides]; [sl[i + 1], sl[i]] = [sl[i], sl[i + 1]]; return { ...f, slides: sl, image_url: sl[0] }; })} className="flex h-7 w-7 items-center justify-center border border-bone/15 font-mono text-xs text-bone-dim hover:border-brass/60 hover:text-brass disabled:opacity-20" aria-label="→">→</button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                <input data-testid="admin-field-image" placeholder={t("admin.orUrl")} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = e.target.value.trim(); if (v) { setForm((f) => ({ ...f, slides: [...(f.slides || []), v], image_url: f.image_url || v })); e.target.value = ""; } } }} className={`${inputCls} mt-2`} />
              </div>
              <textarea placeholder={t("admin.fDescription")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className={`${inputCls} resize-none sm:col-span-2`} />
              <textarea placeholder={t("admin.fStory")} value={form.story} onChange={(e) => setForm({ ...form, story: e.target.value })} rows={3} className={`${inputCls} resize-none sm:col-span-2`} />
              <p className="mt-2 border-t border-bone/10 pt-4 font-mono text-[9px] tracking-[0.3em] text-bone-muted sm:col-span-2">
                {t("admin.enSection")}
              </p>
              <input data-testid="admin-field-title-en" placeholder={t("admin.fTitleEn")} value={form.title_en || ""} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={inputCls} />
              <input placeholder={t("admin.fMediumEn")} value={form.medium_en || ""} onChange={(e) => setForm({ ...form, medium_en: e.target.value })} className={inputCls} />
              <textarea data-testid="admin-field-description-en" placeholder={t("admin.fDescriptionEn")} value={form.description_en || ""} onChange={(e) => setForm({ ...form, description_en: e.target.value })} rows={3} className={`${inputCls} resize-none sm:col-span-2`} />
              <textarea data-testid="admin-field-story-en" placeholder={t("admin.fStoryEn")} value={form.story_en || ""} onChange={(e) => setForm({ ...form, story_en: e.target.value })} rows={3} className={`${inputCls} resize-none sm:col-span-2`} />
              <label className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] text-bone-dim sm:col-span-2">
                <input
                  data-testid="admin-field-featured"
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  className="accent-[#C5A059]"
                />
                {t("admin.featured")}
              </label>
              <div className="flex gap-3 sm:col-span-2">
                <button
                  data-testid="admin-artwork-save"
                  type="submit"
                  className="border border-brass/60 bg-brass/10 px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-brass transition-all hover:bg-brass hover:text-ink"
                >
                  {t("admin.save")}
                </button>
                <button
                  data-testid="admin-artwork-cancel"
                  type="button"
                  onClick={() => setForm(null)}
                  className="border border-bone/15 px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-bone-dim hover:text-bone"
                >
                  {t("admin.cancel")}
                </button>
              </div>
            </form>
          )}

          <div data-testid="admin-artwork-list" className="divide-y divide-bone/10 border-y border-bone/10">
            {artworks.map((art) => (
              <div key={art.artwork_id} className="flex flex-wrap items-center gap-4 py-4" data-testid={`admin-row-${art.artwork_id}`}>
                <div className="h-14 w-12 shrink-0 overflow-hidden border border-bone/10 bg-ink">
                  {art.image_url && <img src={art.image_url} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-serif text-lg text-bone">{art.title}</p>
                  <p className="font-mono text-[9px] tracking-[0.2em] text-bone-muted">
                    {art.dwg_no} · {art.dimensions} · {art.availability}
                  </p>
                </div>
                <span className="font-mono text-sm text-brass">{eur(art.price)}</span>
                <button
                  data-testid={`admin-toggle-availability-${art.artwork_id}`}
                  onClick={() => toggleAvailability(art)}
                  className={`border px-3 py-2 font-mono text-[9px] tracking-[0.25em] transition-colors ${
                    art.availability === "In Atelier"
                      ? "border-brass/50 text-brass hover:bg-brass hover:text-ink"
                      : "border-bone/15 text-bone-muted hover:border-bone/40 hover:text-bone"
                  }`}
                  title={t("admin.toggleAvailHint")}
                >
                  {art.availability === "In Atelier" ? t("admin.availableBtn") : t("admin.unavailableBtn")}
                </button>
                <button
                  data-testid={`admin-edit-${art.artwork_id}`}
                  onClick={() => setForm({ ...EMPTY_FORM, ...art, slides: art.slides?.length ? art.slides : (art.image_url ? [art.image_url] : []) })}
                  className="border border-bone/15 p-2 text-bone-dim transition-colors hover:border-brass/50 hover:text-brass"
                  aria-label="Modifier"
                >
                  <Pencil size={13} />
                </button>
                <button
                  data-testid={`admin-delete-${art.artwork_id}`}
                  onClick={() => deleteArtwork(art.artwork_id)}
                  className="border border-bone/15 p-2 text-bone-dim transition-colors hover:border-red-400/50 hover:text-red-300"
                  aria-label="Supprimer"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div data-testid="admin-orders-list" className="mt-10 space-y-6">
          {orders.length === 0 && (
            <p className="font-mono text-[11px] tracking-[0.3em] text-bone-muted">{t("admin.noOrders")}</p>
          )}
          {orders.map((o) => (
            <div key={o.order_id} className="border border-bone/10 bg-ink-2 p-6" data-testid={`admin-order-${o.order_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-xs tracking-[0.2em] text-brass">{o.order_id}</p>
                <div className="flex items-center gap-3">
                  <span
                    data-testid={`admin-order-status-${o.order_id}`}
                    className={`border px-2 py-1 font-mono text-[9px] tracking-[0.25em] ${
                      o.payment_status === "paid" ? "border-brass/50 text-brass" : "border-bone/15 text-bone-muted"
                    }`}
                  >
                    {(o.status || "").toUpperCase()}
                  </span>
                  <p className="font-serif text-xl text-bone">{eur(o.total)}</p>
                </div>
              </div>
              <p className="mt-3 font-mono text-[10px] tracking-[0.15em] text-bone-dim">
                {o.customer?.name || "—"} — {o.customer?.email || "—"} · {[o.customer?.address, o.customer?.postal_code, o.customer?.city, o.customer?.state, o.customer?.country].filter(Boolean).join(", ")}
              </p>
              {o.shipping_quote && (
                <p data-testid={`admin-order-shipping-${o.order_id}`} className="mt-1 font-mono text-[9px] tracking-[0.15em] text-bone-muted">
                  {o.shipping_quote.fallback ? t("checkout.fallbackService") : `UPS ${o.shipping_quote.service}`} {eur(o.shipping_quote.shipping_eur)} + {t("checkout.handling").toLowerCase()} {eur(o.shipping_quote.handling_fee)} = {eur(o.shipping_quote.total_eur)}
                  {o.shipping_quote.ups_currency && o.shipping_quote.ups_currency !== "EUR" ? ` (${o.shipping_quote.ups_amount} ${o.shipping_quote.ups_currency})` : ""}
                </p>
              )}
              <ul className="mt-3 space-y-1">
                {o.items.map((i) => (
                  <li key={i.artwork_id} className="font-mono text-[10px] tracking-[0.1em] text-bone-muted">
                    • {i.title}{i.dwg_no ? ` (${i.dwg_no})` : ""}
                  </li>
                ))}
              </ul>
              <p className="mt-3 border-t border-bone/10 pt-3 font-mono text-[9px] tracking-[0.25em] text-bone-muted">
                {o.certificate_no} · {new Date(o.created_at).toLocaleString("fr-FR")}
              </p>
            </div>
          ))}
        </div>
      )}

      {tab === "inquiries" && (
        <div data-testid="admin-inquiries-list" className="mt-10 space-y-6">
          {inquiries.length === 0 && (
            <p className="font-mono text-[11px] tracking-[0.3em] text-bone-muted">{t("admin.noInquiries")}</p>
          )}
          {inquiries.map((q) => (
            <div key={q.inquiry_id} className="border border-bone/10 bg-ink-2 p-6" data-testid={`admin-inquiry-${q.inquiry_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-mono text-xs tracking-[0.2em] text-brass">{q.inquiry_type}</p>
                <p className="font-mono text-[9px] tracking-[0.2em] text-bone-muted">
                  {new Date(q.created_at).toLocaleString("fr-FR")}
                </p>
              </div>
              <p className="mt-3 font-serif text-lg text-bone">
                {q.name} <span className="font-mono text-[10px] text-bone-muted">— {q.email}</span>
              </p>
              {(q.archetype || q.canvas_scale) && (
                <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-bone-dim">
                  {q.archetype && <>{t("admin.archetype")} {q.archetype} </>}
                  {q.canvas_scale && <>· {t("admin.format")} {q.canvas_scale}</>}
                </p>
              )}
              <p className="mt-3 text-sm leading-relaxed text-bone-dim/85">{q.message}</p>
            </div>
          ))}
        </div>
      )}
      {tab === "subscribers" && (
        <div data-testid="admin-subscribers-list" className="mt-10">
          {subscribers.length === 0 ? (
            <p className="font-mono text-[11px] tracking-[0.3em] text-bone-muted">{t("admin.noSubscribers")}</p>
          ) : (
            <>
              <button
                data-testid="admin-subscribers-export"
                onClick={exportCsv}
                className="mb-6 flex items-center gap-2 border border-brass/60 bg-brass/10 px-6 py-3 font-mono text-[10px] tracking-[0.3em] text-brass transition-all hover:bg-brass hover:text-ink"
              >
                <Download size={13} /> {t("admin.exportCsv")} ({subscribers.length})
              </button>
              <div className="divide-y divide-bone/10 border-y border-bone/10">
                {subscribers.map((s) => (
                  <div key={s.subscriber_id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`admin-subscriber-${s.subscriber_id}`}>
                    <span className="font-mono text-xs tracking-[0.1em] text-bone">{s.email}</span>
                    <span className="font-mono text-[9px] tracking-[0.2em] text-bone-muted">
                      {s.lang.toUpperCase()} · {new Date(s.created_at).toLocaleString("fr-FR")}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "shipping" && <AdminShipping headers={headers} />}

      {tab === "site" && settings && (
        <div className="mt-10 space-y-8">
        <div data-testid="admin-site-panel" className="border border-bone/10 bg-ink-2 p-6 sm:p-8">
          <p className="font-mono text-[10px] tracking-[0.3em] text-brass">{t("admin.sitePhotoTitle")}</p>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-bone-dim/80">{t("admin.sitePhotoHint")}</p>
          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            <div className="relative aspect-[4/3] overflow-hidden border border-bone/10 bg-ink">
              {settings.atelier_photo_url ? (
                <img data-testid="admin-site-photo-preview" src={settings.atelier_photo_url} alt="" className="h-full w-full object-cover" />
              ) : (
                <div data-testid="admin-site-photo-empty" className="flex h-full items-center justify-center bp-grid-fine font-mono text-[10px] tracking-[0.3em] text-bone-muted">
                  {t("admin.sitePhotoEmpty")}
                </div>
              )}
            </div>
            <div className="flex flex-wrap content-start gap-3">
              <label className={`flex items-center gap-2 border border-brass/50 px-4 py-2.5 font-mono text-[10px] tracking-[0.25em] text-brass transition-colors ${siteUploading ? "opacity-50" : "cursor-pointer hover:bg-brass hover:text-ink"}`}>
                <Upload size={12} />
                {siteUploading ? t("admin.uploading") : t("admin.uploadLabel")}
                <input
                  data-testid="admin-site-photo-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                  className="hidden"
                  onChange={uploadSitePhoto}
                  disabled={siteUploading}
                />
              </label>
              {settings.atelier_photo_url && (
                <button
                  data-testid="admin-site-photo-remove"
                  type="button"
                  onClick={() => saveSettings({ atelier_photo_url: "" })}
                  className="border border-bone/15 px-4 py-2.5 font-mono text-[10px] tracking-[0.25em] text-bone-dim transition-colors hover:border-red-400/50 hover:text-red-300"
                >
                  {t("admin.sitePhotoRemove")}
                </button>
              )}
            </div>
          </div>
        </div>

        <form
          data-testid="admin-contacts-form"
          onSubmit={(e) => { e.preventDefault(); saveSettings({}, t("admin.settingsSaved")); }}
          className="border border-bone/10 bg-ink-2 p-6 sm:p-8"
        >
          <p className="font-mono text-[10px] tracking-[0.3em] text-brass">{t("admin.contactsTitle")}</p>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-bone-dim/80">{t("admin.contactsHint")}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <input data-testid="admin-field-contact-email" type="email" placeholder={t("admin.fContactEmail")} value={settings.contact_email || ""} onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })} className={inputCls} />
            <input data-testid="admin-field-contact-phone" placeholder={t("admin.fContactPhone")} value={settings.contact_phone || ""} onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })} className={inputCls} />
            <input data-testid="admin-field-contact-whatsapp" placeholder={t("admin.fContactWhatsapp")} value={settings.contact_whatsapp || ""} onChange={(e) => setSettings({ ...settings, contact_whatsapp: e.target.value })} className={inputCls} />
            <input data-testid="admin-field-contact-hours" placeholder={t("admin.fContactHours")} value={settings.contact_hours || ""} onChange={(e) => setSettings({ ...settings, contact_hours: e.target.value })} className={inputCls} />
            <textarea data-testid="admin-field-contact-address" placeholder={t("admin.fContactAddress")} value={settings.contact_address || ""} onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })} rows={3} className={`${inputCls} resize-none sm:col-span-2`} />
          </div>
          <button data-testid="admin-contacts-save" type="submit" className="mt-6 border border-brass/60 bg-brass/10 px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-brass transition-all hover:bg-brass hover:text-ink">
            {t("admin.saveSettings")}
          </button>
        </form>

        <form
          data-testid="admin-visibility-form"
          onSubmit={(e) => { e.preventDefault(); saveSettings({}, t("admin.settingsSaved")); }}
          className="border border-bone/10 bg-ink-2 p-6 sm:p-8"
        >
          <p className="font-mono text-[10px] tracking-[0.3em] text-brass">{t("admin.visibilityTitle")}</p>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-bone-dim/80">{t("admin.visibilityHint")}</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <input data-testid="admin-field-site-url" type="url" placeholder={t("admin.fSiteUrl")} value={settings.site_url || ""} onChange={(e) => setSettings({ ...settings, site_url: e.target.value })} className={`${inputCls} sm:col-span-2`} />
            <input data-testid="admin-field-google" placeholder={t("admin.fGoogle")} value={settings.google_site_verification || ""} onChange={(e) => setSettings({ ...settings, google_site_verification: e.target.value })} className={`${inputCls} sm:col-span-2`} />
            {SOCIALS.map((k) => (
              <input key={k} data-testid={`admin-field-social-${k}`} type="url" placeholder={`${k.toUpperCase()} — https://…`} value={settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} className={inputCls} />
            ))}
          </div>
          <button
            data-testid="admin-visibility-save"
            type="submit"
            className="mt-6 border border-brass/60 bg-brass/10 px-8 py-3 font-mono text-[10px] tracking-[0.3em] text-brass transition-all hover:bg-brass hover:text-ink"
          >
            {t("admin.saveSettings")}
          </button>
        </form>

        <div data-testid="admin-seo-help" className="border border-bone/10 bg-ink-2 p-6 sm:p-8">
          <p className="font-mono text-[10px] tracking-[0.3em] text-brass">{t("admin.seoHelpTitle")}</p>
          <dl className="mt-4 space-y-4 text-sm text-bone-dim/80">
            <div>
              <dt className="font-mono text-[9px] tracking-[0.25em] text-bone-muted">{t("admin.seoHelp1")}</dt>
              <dd className="mt-1 break-all font-mono text-xs text-bone" data-testid="admin-sitemap-url">{publicBase}/api/sitemap.xml</dd>
            </div>
            <div>
              <dt className="font-mono text-[9px] tracking-[0.25em] text-bone-muted">{t("admin.seoHelp2")}</dt>
              <dd className="mt-1 break-all font-mono text-xs text-bone" data-testid="admin-share-home-url">{publicBase}/api/share/home</dd>
            </div>
            <div>
              <dd className="text-xs leading-relaxed text-bone-dim/70">{t("admin.seoHelp3")}</dd>
            </div>
          </dl>
        </div>
        </div>
      )}
    </div>
  );
}
